import * as React from 'react';

import type { AjaxError } from 'rxjs';
import { isForgeFixUndefinedExtensionErrorEnalbled } from '@helpcenter/feature-flags';
import { AtlassianHelpConfigAccountType } from '@helpcenter/forge-ui-utils';
import type {
	Extension,
	ExtensionPointModule,
	ForgePageName,
	ResourceContext,
} from '@helpcenter/forge-ui-utils';
import {
	RESOURCE_TYPE_ISSUE,
	RESOURCE_TYPE_PROJECT,
} from '@helpcenter/forge-ui-utils/src/constants';
import { getMetaFromHTML } from '@helpcenter/meta';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { OPERATIONAL_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { trackError } from '@atlassian/help-center-common-util/analytics';

import { TFECacheContext } from '../tfe-cache-context';

export const useTFExtensionList = ({
	type,
	resourceContext,
	page,
}: {
	type: ExtensionPointModule;
	resourceContext?: ResourceContext;
	page?: ForgePageName;
}) => {
	const [extensions, setExtensions] = React.useState<Extension[]>([]);
	// Tracks whether the availability fetch has settled (success or failure).
	// Consumers use this to distinguish "no apps installed" from "still
	// loading" — e.g. to decide when to show an editor empty state — without
	// relying on fragile reference-identity heuristics.
	const [resolved, setResolved] = React.useState(false);
	const accountType =
		getMetaFromHTML('ajs-account-type') || AtlassianHelpConfigAccountType.ANONYMOUS;
	const { getExtensionsByType, getExtensionsByTypeV2 } = React.useContext(TFECacheContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	React.useEffect(() => {
		if (
			accountType === AtlassianHelpConfigAccountType.ATLASSIAN ||
			accountType === AtlassianHelpConfigAccountType.ANONYMOUS ||
			accountType === AtlassianHelpConfigAccountType.CUSTOMER
		) {
			const { resourceType } = resourceContext || {};
			if (resourceType === RESOURCE_TYPE_ISSUE || resourceType === RESOURCE_TYPE_PROJECT) {
				getExtensionsByTypeV2(type, resourceContext, page)
					.then((fetchedExtensions) => {
						setExtensions(
							isForgeFixUndefinedExtensionErrorEnalbled()
								? fetchedExtensions[type] || []
								: fetchedExtensions[type],
						);
					})
					.catch((e: AjaxError) => {
						trackError('forge-tfe.setState.failed', {}, e);
					})
					.finally(() => {
						setResolved(true);
					});
			} else {
				getExtensionsByType(type, page)
					.then((fetchedExtensions) => {
						setExtensions(
							isForgeFixUndefinedExtensionErrorEnalbled()
								? fetchedExtensions[type] || []
								: fetchedExtensions[type],
						);
					})
					.catch((e: AjaxError) => {
						trackError('forge-tfe.setState.failed', {}, e);
					})
					.finally(() => {
						setResolved(true);
					});
			}
		} else {
			// Account type that never fetches: treat as immediately resolved so
			// consumers do not wait forever for a request that will not happen.
			setResolved(true);
		}
	}, [
		accountType,
		getExtensionsByType,
		getExtensionsByTypeV2,
		type,
		resourceContext,
		page,
		createAnalyticsEvent,
	]);

	React.useEffect(() => {
		extensions.forEach((extension) => {
			const { id, dataClassificationPolicyDecision } = extension;
			const event = createAnalyticsEvent({
				actionSubject: 'forgeTFE',
				action: 'fetched',
				analyticsType: OPERATIONAL_EVENT_TYPE,
				attributes: {
					page,
					moduleType: type,
					status: dataClassificationPolicyDecision?.status,
					extensionId: id,
				},
			});
			event.context.push({ componentName: 'forgeTFE' });
			event.fire();
		});
	}, [extensions, page, createAnalyticsEvent, type]);

	return {
		extensions,
		resolved,
	};
};
